/**
 * Blue Zone — unified toast (wizard-2 + Firmhouse portal custom toasts).
 * Depends on: window.bzToastLocales (optional for showKey).
 * Sync: assets/bz-toast.js, shared_head.liquid
 */
(function (global) {
  'use strict';

  var DURATION_MS = 6000;
  var CROSS_DEFAULT =
    'https://cdn.shopify.com/s/files/1/0919/3534/2856/files/cross-neutral-600.svg?v=1782379412';
  var CROSS_ACTIVE =
    'https://cdn.shopify.com/s/files/1/0919/3534/2856/files/cross-neutral-800.svg?v=1782379412';

  var queue = [];
  var active = null;
  var hideTimer = null;
  var fadeTimer = null;

  function locale() {
    var fromPath = (window.location.pathname.split('/').filter(Boolean)[0] || '').toLowerCase().slice(0, 2);
    if (fromPath === 'en' || fromPath === 'nl') return fromPath;
    var cfg = global.bzPortalConfig && global.bzPortalConfig.locale;
    if (cfg) return String(cfg).toLowerCase().slice(0, 2) === 'en' ? 'en' : 'nl';
    var lang = (document.documentElement.lang || 'nl').toLowerCase();
    return lang.indexOf('en') === 0 ? 'en' : 'nl';
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function fill(template, vars) {
    var out = String(template || '');
    Object.keys(vars || {}).forEach(function (k) {
      out = out.split('{' + k + '}').join(vars[k] != null ? String(vars[k]) : '');
    });
    return out;
  }

  function t(key, vars, localeOverride) {
    var L = localeOverride || locale();
    var pack = (global.bzToastLocales && (global.bzToastLocales[L] || global.bzToastLocales.nl)) || {};
    var raw = pack[key];
    if (typeof raw !== 'string') return key;
    return fill(raw, vars);
  }

  function bottomOffset() {
    var nav = document.querySelector('.portal-bottom-nav');
    var preview = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--firmhouse-preview-bar-height'),
      10
    );
    var previewPx = isNaN(preview) ? 0 : preview;
    var navPx = 0;
    if (nav && nav.offsetParent !== null) {
      navPx = nav.getBoundingClientRect().height || 72;
    }
    var quizFooter = document.querySelector('.quiz-footer');
    var footerPx = 0;
    if (quizFooter) {
      var rect = quizFooter.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom >= window.innerHeight - 4) {
        footerPx = rect.height;
      }
    }
    var extra = Math.max(navPx, footerPx) + previewPx;
    return extra + 16 + 'px';
  }

  function ensureHost() {
    var host = document.getElementById('bz-toast-host');
    if (!host) {
      host = document.createElement('div');
      host.id = 'bz-toast-host';
      host.className = 'bz-toast-host';
      host.setAttribute('aria-live', 'polite');
      host.setAttribute('aria-relevant', 'additions');
      document.body.appendChild(host);
    }
    host.style.setProperty('--bz-toast-bottom', bottomOffset());
    return host;
  }

  function clearTimers() {
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
    if (fadeTimer) {
      clearTimeout(fadeTimer);
      fadeTimer = null;
    }
  }

  function dismissActive() {
    if (!active || !active.el) return;
    clearTimers();
    active.el.classList.remove('is-visible');
    var el = active.el;
    fadeTimer = setTimeout(function () {
      if (el.parentNode) el.parentNode.removeChild(el);
      active = null;
      showNext();
    }, 280);
  }

  function showNext() {
    if (active || !queue.length) return;
    var item = queue.shift();
    renderToast(item);
  }

  function enqueue(item) {
    queue.push(item);
    showNext();
  }

  function renderToast(item) {
    var host = ensureHost();
    var type = item.type || 'success';
    var toast = document.createElement('div');
    toast.className = 'bz-toast bz-toast--' + type;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

    var body = document.createElement('div');
    body.className = 'bz-toast__body';
    if (item.html) {
      body.innerHTML = item.html;
    } else {
      body.textContent = item.message || '';
    }
    toast.appendChild(body);

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'bz-toast__close';
    closeBtn.setAttribute('aria-label', t('close'));
    closeBtn.innerHTML =
      '<img class="bz-toast__close-icon bz-toast__close-icon--default" src="' +
      CROSS_DEFAULT +
      '" alt="" width="14" height="14" aria-hidden="true">' +
      '<img class="bz-toast__close-icon bz-toast__close-icon--active" src="' +
      CROSS_ACTIVE +
      '" alt="" width="14" height="14" aria-hidden="true">';
    closeBtn.addEventListener('click', function () {
      dismissActive();
    });
    toast.appendChild(closeBtn);

    host.appendChild(toast);
    active = { el: toast, item: item };

    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });

    var duration = item.duration != null ? item.duration : DURATION_MS;
    if (duration > 0) {
      hideTimer = setTimeout(dismissActive, duration);
    }

    if (typeof item.onMount === 'function') {
      item.onMount(toast, dismissActive);
    }
  }

  function show(opts) {
    if (!opts) return;
    if (typeof opts === 'string') {
      opts = { message: opts };
    }
    enqueue({
      type: opts.type || 'success',
      message: opts.message || '',
      html: opts.html,
      duration: opts.duration,
      onMount: opts.onMount,
    });
  }

  function showKey(key, vars, type, localeOverride) {
    var typeMap = {
      photo_saved: 'success',
      photo_upload_failed: 'error',
      photo_too_large: 'warning',
      photo_profile_not_loaded: 'warning',
      photo_upload_unavailable: 'warning',
      skip_email_error: 'error',
      invalid_pet_data: 'error',
      account_creation_error: 'error',
      cart_checkout_error: 'error',
      profile_saved: 'success',
      profile_removed: 'success',
      dog_removed: 'success',
      order_summary_error: 'error',
      date_changed: 'success',
      frequency_changed: 'success',
      date_no_order: 'warning',
      quantity_changed: 'success',
      product_removed: 'success',
      quantity_update_error: 'error',
      product_remove_error: 'error',
      plan_updated: 'success',
      plan_update_error: 'error',
      product_added_plan: 'success',
      product_added_next: 'success',
      link_copied: 'success',
      instagram_fallback: 'success',
      copy_error: 'error',
      referral_load_error: 'error',
      pause_success: 'success',
      resume_success: 'success',
      skip_success: 'success',
      discount_applied: 'success',
      discount_apply_error: 'error',
      cancel_action_error: 'error',
      reactivate_success: 'success',
      reactivate_already_active: 'success',
      reactivate_plan_not_found: 'error',
      reactivate_error: 'error',
    };
    show({
      type: type || typeMap[key] || 'success',
      message: t(key, vars, localeOverride),
    });
  }

  function setPending(payload) {
    try {
      sessionStorage.setItem('bz_toast_pending', JSON.stringify(payload));
    } catch (e) {}
  }

  function applyPending() {
    try {
      if (!document.body) return false;

      if (sessionStorage.getItem('bz_bulk_toast') === 'success') {
        sessionStorage.removeItem('bz_bulk_toast');
        showKey('plan_updated');
        return true;
      }
      if (sessionStorage.getItem('bz_half_toast') === 'success') {
        sessionStorage.removeItem('bz_half_toast');
        showKey('plan_updated');
        return true;
      }

      var raw = sessionStorage.getItem('bz_toast_pending');
      if (!raw) return false;
      var data = JSON.parse(raw);
      if (!data || !data.key) {
        sessionStorage.removeItem('bz_toast_pending');
        return false;
      }
      // Keep pending until render succeeds so turbo/late boots can retry.
      showKey(data.key, data.vars || {}, data.type, data.locale);
      sessionStorage.removeItem('bz_toast_pending');
      return true;
    } catch (e) {
      return false;
    }
  }

  function scheduleApplyPending() {
    function run() {
      applyPending();
    }
    if (!document.body || document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
      return;
    }
    run();
    // One short retry for late portal boots — avoid turbo:render (it can
    // flush pending during replaceState right before a reload and lose the toast).
    window.setTimeout(run, 50);
  }

  function applyEntryQueryParams() {
    try {
      var ENTRY_TO_KEY = 'bz_entry_to';
      var ENTRY_SID_KEY = 'bz_entry_sid';
      var params = new URLSearchParams(window.location.search || '');
      var toastKey = params.get('bz_toast');
      var to = params.get('bz_to');
      var sid = params.get('bz_sid');
      var bzLang = params.get('bz_lang');
      var hashRaw = String(window.location.hash || '').replace(/^#/, '');
      var hashQi = hashRaw.indexOf('?');
      var pathAndSid = (hashQi >= 0 ? hashRaw.slice(0, hashQi) : hashRaw).trim();
      var hashParams = new URLSearchParams(hashQi >= 0 ? hashRaw.slice(hashQi + 1) : '');
      var tilde = pathAndSid.lastIndexOf('~');
      var hashPath = pathAndSid;
      if (tilde >= 0) {
        if (!sid) sid = pathAndSid.slice(tilde + 1).trim();
        hashPath = pathAndSid.slice(0, tilde).trim();
      }
      if (!sid) sid = hashParams.get('bz_sid');
      var onPlanPicker = /\/subscription_sessions(\/|$)/.test(String(window.location.pathname || ''));
      var changed = false;

      function normalizeEntryTo(value) {
        var cleaned = String(value || '')
          .trim()
          .replace(/^#/, '')
          .replace(/^\//, '');
        cleaned = cleaned.replace(/^(nl|en)\/self-service\/?/i, '');
        cleaned = cleaned.replace(/^self-service\/?/i, '');
        if (cleaned === 'free_food') return 'free-food';
        if (cleaned === 'bulk_save') return 'bulk-save';
        if (cleaned === 'half_plan') return 'half-plan';
        return cleaned;
      }

      function isAllowlistedTo(value) {
        var cleaned = normalizeEntryTo(value);
        if (!cleaned || cleaned === 'home') return true;
        if (/^[a-z][a-z0-9+.-]*:/i.test(cleaned)) return false;
        if (cleaned.indexOf('//') === 0 || cleaned.indexOf('..') >= 0) return false;
        return /^[a-zA-Z0-9][a-zA-Z0-9\/_-]{0,200}$/.test(cleaned);
      }

      if (bzLang) {
        var bl = String(bzLang || '').toLowerCase().slice(0, 2);
        if (bl === 'en' || bl === 'nl') {
          try {
            sessionStorage.setItem('bz_portal_locale_redirected', '1');
          } catch (e) {}
        }
        params.delete('bz_lang');
        changed = true;
      }

      if (toastKey) {
        setPending({ key: toastKey });
        params.delete('bz_toast');
        changed = true;
      }

      if (sid) {
        try {
          sessionStorage.setItem(ENTRY_SID_KEY, String(sid).trim());
        } catch (e) {}
        if (params.has('bz_sid')) {
          params.delete('bz_sid');
          changed = true;
        }
        if (hashParams.has('bz_sid')) {
          hashParams.delete('bz_sid');
          changed = true;
        }
        // Strip ~sid from visible hash after stashing (route parsers expect a clean path).
        if (tilde >= 0) changed = true;
      }

      var target = normalizeEntryTo(to || hashPath || '');
      if (!target) {
        try {
          target = normalizeEntryTo(sessionStorage.getItem(ENTRY_TO_KEY) || '');
        } catch (e) {}
      }

      if (isAllowlistedTo(target)) {
        try {
          sessionStorage.setItem(ENTRY_TO_KEY, target);
        } catch (e) {}
        if (to) {
          params.delete('bz_to');
          changed = true;
        }
        if (!onPlanPicker) {
          // Keep ENTRY_TO until applyStoredEntryHash / dashboard boot consumes it.
          hashPath = target;
          changed = true;
        } else if (tilde >= 0 || hashParams.toString()) {
          // On plan picker, keep clean route hash for humans; sid lives in sessionStorage.
          changed = true;
        }
      }

      if (changed) {
        var hashQs = hashParams.toString();
        var nextHash = '';
        if (hashPath) nextHash = '#' + hashPath + (hashQs ? '?' + hashQs : '');
        else if (hashQs) nextHash = '#?' + hashQs;
        var qs = params.toString();
        var next = window.location.pathname + (qs ? '?' + qs : '') + nextHash;
        window.history.replaceState(null, '', next);
      }

      if (!onPlanPicker && window.bzPortalApplyStoredEntryHash) {
        window.bzPortalApplyStoredEntryHash();
      }
    } catch (e) {}
  }

  global.bzToast = {
    show: show,
    showKey: showKey,
    t: t,
    locale: locale,
    dismiss: dismissActive,
    setPending: setPending,
    applyPending: applyPending,
    scheduleApplyPending: scheduleApplyPending,
    applyEntryQueryParams: applyEntryQueryParams,
    DURATION_MS: DURATION_MS,
  };

  applyEntryQueryParams();
  scheduleApplyPending();
  document.addEventListener('turbo:load', scheduleApplyPending);
  window.addEventListener('pageshow', scheduleApplyPending);
})(typeof window !== 'undefined' ? window : this);
